import { useCallback, useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  motion as m,
  useAnimationControls,
} from "motion/react";
import QRCode from "qrcode";
import "./tv.css";
import type { GuessResult } from "../../shared/game-types.js";
import {
  request,
  useConnection,
  useCountdown,
  useGuessResults,
  useRoom,
} from "../lib/socket.js";
import { useAudioTuning, useMotionTuning, useRules } from "../lib/tuning.js";
import { Board } from "./Board.js";
import { Hud } from "./Hud.js";
import { NextUpBar } from "./NextUpBar.js";
import { BanBoard } from "./BanBoard.js";
import { DriftMarks } from "../DriftMarks.js";
import { matchSettings, settingsStore } from "../lib/settings.js";
import { Confetti } from "./Confetti.js";
import { Tuner } from "../tuner/Tuner.js";
import { playCue, unlockAudio } from "../audio/audio.js";

/**
 * Verdicts that put a guess on the "already tried" strip.
 *
 * A near miss belongs — it was a real answer that this board does not hold. A
 * duplicate does not: it is on the board, found by someone else.
 */
const REFUSED = new Set<GuessResult["verdict"]>(["wrong", "near-miss"]);

const CALLOUTS: Record<
  GuessResult["verdict"],
  { word: string; tone: "good" | "bad" | "warn" }
> = {
  correct: { word: "Yes", tone: "good" },
  duplicate: { word: "Already up", tone: "warn" },
  "near-miss": { word: "So close", tone: "warn" },
  wrong: { word: "No", tone: "bad" },
  timeout: { word: "Time's up", tone: "bad" },
  pass: { word: "Passed", tone: "bad" },
};

const LOOPBACK = new Set(["localhost", "127.0.0.1", "[::1]", "::1", ""]);

/**
 * An origin a phone in the room can actually open.
 *
 * The TV is usually cast from a browser sitting on localhost, and a QR code
 * encoding "localhost" sends the phone to itself. When the page is on loopback
 * we ask the server which LAN address it can be reached on and rebuild the
 * origin around it, keeping the port the page is already served from — in dev
 * that is Vite's, not the API's.
 */
async function reachableOrigin(): Promise<string> {
  if (!LOOPBACK.has(window.location.hostname)) return window.location.origin;
  try {
    const res = await fetch("/api/network");
    if (!res.ok) return window.location.origin;
    const { addresses } = (await res.json()) as { addresses: string[] };
    const host = addresses[0];
    if (!host) return window.location.origin;
    const port = window.location.port ? `:${window.location.port}` : "";
    return `${window.location.protocol}//${host}${port}`;
  } catch {
    // No network route discoverable — the on-screen URL is still correct for
    // anyone on this machine.
    return window.location.origin;
  }
}

export function TvApp({
  /** Told when the TV is parked in the lobby, so the shell can offer a way out. */
  onIdle,
}: { onIdle?: (idle: boolean) => void } = {}) {
  const room = useRoom();
  const connection = useConnection();
  const rules = useRules();
  const motion = useMotionTuning();
  const audioTuning = useAudioTuning();
  const [error, setError] = useState<string | null>(null);
  const [joinUrl, setJoinUrl] = useState("");
  const [qr, setQr] = useState("");
  const [result, setResult] = useState<GuessResult | null>(null);
  /** Everything typed and refused on the current category. */
  const [misses, setMisses] = useState<GuessResult[]>([]);
  const [confettiTrigger, setConfettiTrigger] = useState(0);
  const calloutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shakeControls = useAnimationControls();

  const shakeTuning = useMotionTuning().wrong;
  const shake = useCallback(() => {
    const { shakeAmplitude_px, shakeDuration_ms } = shakeTuning;
    void shakeControls.start({
      x: [0, -shakeAmplitude_px, shakeAmplitude_px, 0],
      transition: { duration: shakeDuration_ms / 1000 },
    });
  }, [shakeControls, shakeTuning]);

  const remaining = useCountdown(
    room?.phase === "playing" ? room.deadline : null,
  );

  // Create or re-attach a room. A TV refresh must not orphan a live match, so
  // the code is kept in the URL and re-attached to on load.
  useEffect(() => {
    const existing = new URLSearchParams(window.location.search).get("room");
    const go = async () => {
      try {
        const data = existing
          ? await request<{ code: string }>("tv:attach", { code: existing })
          : await request<{ code: string }>("tv:create", {
              settings: matchSettings(settingsStore.get()),
            });
        const url = new URL(window.location.href);
        url.searchParams.set("room", data.code);
        window.history.replaceState({}, "", url);
        const joinAt = `${await reachableOrigin()}/?room=${data.code}`;
        setJoinUrl(joinAt);
        setQr(
          await QRCode.toDataURL(joinAt, {
            margin: 0,
            width: 480,
            color: { dark: "#1B1D22", light: "#FFFFFF" },
          }),
        );
      } catch (e) {
        setError((e as Error).message);
      }
    };
    if (connection === "online") void go();
  }, [connection]);

  useGuessResults(
    useCallback((r: GuessResult) => {
      setResult(r);
      // Only answers this board actually refused.
      //
      // `correct` is on the board, and so is `duplicate` — someone else already
      // found it, which is the opposite of a failed guess, and listing it struck
      // through next to the slot it filled reads as the game contradicting
      // itself. `timeout` and `pass` carry no typed text at all.
      if (REFUSED.has(r.verdict) && r.raw.trim()) {
        setMisses((prev) =>
          prev.some((m) => m.raw.toLowerCase() === r.raw.toLowerCase())
            ? prev
            : [...prev, r],
        );
      }
      if (r.verdict === "correct") setConfettiTrigger((n) => n + 1);
      else shake();
      playCue(
        r.verdict === "near-miss"
          ? "nearMiss"
          : r.verdict === "timeout"
            ? "timeout"
            : r.verdict,
        r.rank ?? 0,
      );
      if (calloutTimer.current) clearTimeout(calloutTimer.current);
      calloutTimer.current = setTimeout(() => setResult(null), 1600);
    }, []),
  );

  const phase = room?.phase;
  useEffect(() => {
    onIdle?.(phase === undefined || phase === "lobby");
  }, [phase, onIdle]);

  // Wrong answers belong to the category they were wrong about.
  const categoryIndex = room?.category?.index;
  useEffect(() => setMisses([]), [categoryIndex]);

  // Browsers keep an AudioContext suspended until the page has been interacted
  // with, and a cast TV may never be touched at all — so take the first event
  // of any kind, and show a nudge in the lobby while it is still silent.
  useEffect(() => {
    const unlock = () => unlockAudio();
    const events = ["pointerdown", "keydown", "touchstart"] as const;
    for (const e of events) window.addEventListener(e, unlock, { once: false });
    return () => {
      for (const e of events) window.removeEventListener(e, unlock);
    };
  }, []);

  // One cue per phase transition, fired on the change rather than on every
  // render so a reconnect that replays state does not retrigger the fanfare.
  const lastPhase = useRef(room?.phase);
  useEffect(() => {
    const phase = room?.phase;
    if (!phase || phase === lastPhase.current) return;
    lastPhase.current = phase;
    if (phase === "category-intro") playCue("categoryIn");
    if (phase === "match-end") playCue("matchEnd");
  }, [room?.phase]);

  // The closing cascade: one rising note per slot as it flips, matched to the
  // stagger the board animates on, so picture and sound land together.
  const cascadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (room?.phase !== "category-reveal" || !room.category) return;
    if (cascadedFor.current === room.category.id) return;
    cascadedFor.current = room.category.id;
    const unearned = room.category.slots.filter((s) => !s.foundBy);
    const cleared = unearned.length === 0;
    if (cleared) return void playCue("clearBoard");
    const timers = unearned.map((_, i) =>
      setTimeout(
        () => playCue("reveal", i),
        i * motion.reveal.staggerFromClose_ms,
      ),
    );
    return () => timers.forEach(clearTimeout);
  }, [room?.phase, room?.category, motion.reveal.staggerFromClose_ms]);

  // A tick per second once the clock is nearly out — the sound the timer bar
  // cannot make.
  const lastTick = useRef(-1);
  useEffect(() => {
    if (room?.phase !== "playing") return;
    const whole = Math.ceil(remaining);
    if (whole === lastTick.current) return;
    lastTick.current = whole;
    const audio = audioTuning;
    if (whole > 0 && whole <= audio.tick.startsAt_s) playCue("tick");
  }, [remaining, room?.phase, audioTuning]);

  // Space bar advances the intro and reveal cards without a remote.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      if (e.code === "Space") {
        e.preventDefault();
        if (room?.phase === "lobby")
          void request("tv:start", {}).catch((x) => setError(x.message));
        else void request("tv:advance", {});
      }
      // Safety net for a matcher misfire: ] accepts the last guess, [ rejects it.
      if (e.key === "]") void request("tv:override", { accept: true });
      if (e.key === "[") void request("tv:override", { accept: false });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [room?.phase]);

  if (connection !== "online" || !room) {
    return (
      <div className="tv grain">
        <div className="bootScreen">
          <div className="label">Fact Feud</div>
          <div className="prompt display">
            {error ??
              (connection === "online" ? "Opening a room…" : "Connecting…")}
          </div>
        </div>
        <Tuner />
      </div>
    );
  }

  const category = room.category;
  const cascading = room.phase === "category-reveal";
  const callout = result ? CALLOUTS[result.verdict] : null;

  return (
    /*
     * Driven by animation controls rather than by a changing `key`.
     *
     * Re-keying this element to replay the shake tore down and rebuilt the
     * entire TV on every rejected guess. The board replayed its reveals, the
     * clock refilled from zero although the deadline had not moved, and the
     * confetti canvas remounted holding its last non-zero trigger and fired
     * again — so a duplicate guess threw a celebration. Controls replay the
     * animation without touching the tree.
     */
    <m.div className="tv grain" animate={shakeControls}>
      <DriftMarks />
      {room.phase === "lobby" ? (
        <Lobby room={room} qr={qr} joinUrl={joinUrl} error={error} />
      ) : room.phase === "category-ban" ? (
        <BanBoard room={room} />
      ) : room.phase === "match-end" ? (
        <Final room={room} />
      ) : (
        <>
          <header className="tvHead">
            <div>
              <div className="tvHead__meta">
                <span className="chip">
                  {String(room.category ? room.category.index + 1 : 1).padStart(
                    2,
                    "0",
                  )}{" "}
                  / {room.categoryCount}
                </span>
                <span className="chip">{category?.topic}</span>
                <span className="chip">
                  {"◆".repeat(category?.difficulty ?? 1)}
                  <span className="srOnly">
                    difficulty {category?.difficulty}
                  </span>
                </span>
              </div>
              <m.h1
                className="prompt display riso"
                data-text={category?.prompt}
                // Character count drives the length-aware type scale in tv.css,
                // so a 90-character prompt shrinks to fit instead of spilling.
                style={{
                  ["--prompt-len" as string]: category?.prompt.length ?? 40,
                }}
                key={category?.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: motion.category.introDuration_ms / 1000,
                  ease: [0.2, 0.9, 0.2, 1],
                }}
              >
                {category?.prompt}
              </m.h1>
              {category?.subtitle ? (
                <p className="subtitle">{category.subtitle}</p>
              ) : null}
            </div>
          </header>

          <main>
            {category ? (
              <Board
                slots={category.slots}
                justFound={
                  result?.verdict === "correct" ? (result.rank ?? null) : null
                }
                cascading={cascading}
              />
            ) : null}
            {category?.source && cascading ? (
              <p className="sourceLine">
                {category.source.label} · {category.source.asOf}
              </p>
            ) : null}
            {/* The callout that announces a miss is gone in under two seconds,
                which left the room re-suggesting answers that had already been
                refused. Everything tried and rejected on this category stays up
                until the category changes. */}
            {misses.length ? (
              <div className="misses">
                <span className="misses__label">Already tried</span>
                <ul>
                  <AnimatePresence initial={false}>
                    {misses.map((miss) => (
                      <m.li
                        key={miss.seq}
                        className="teamTint"
                        data-team={miss.teamId}
                        data-verdict={miss.verdict}
                        initial={{ opacity: 0, scale: 0.7 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{
                          type: "spring",
                          stiffness: 320,
                          damping: 22,
                        }}
                      >
                        {miss.raw}
                      </m.li>
                    ))}
                  </AnimatePresence>
                </ul>
              </div>
            ) : null}
          </main>

          <Hud
            room={room}
            remaining={remaining}
            answerSeconds={rules.answerSeconds}
          />

          <NextUpBar room={room} />

          <Confetti
            trigger={confettiTrigger}
            teamId={result?.teamId ?? "rose"}
          />

          <AnimatePresence>
            {callout ? (
              <m.div
                className="callout teamTint"
                data-team={result?.teamId ?? "rose"}
                initial={{ opacity: 0, scale: 0.86, y: 14 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 1.04 }}
                transition={{ type: "spring", stiffness: 320, damping: 22 }}
              >
                <div
                  className="callout__word display"
                  style={{
                    color:
                      callout.tone === "good"
                        ? "var(--c-affirm)"
                        : callout.tone === "warn"
                          ? "var(--c-caution)"
                          : "var(--c-rose)",
                  }}
                >
                  {callout.word}
                </div>
                {/* Whatever was typed, echoed back. Without this a rejected
                    guess vanishes and the room never learns what was refused —
                    which also hides matcher misfires worth overriding. */}
                {result?.raw ? (
                  <div className="callout__guess">“{result.raw}”</div>
                ) : null}
                {/* Naming the entries it was torn between would give the board
                    away, so say only that there was more than one. */}
                {result?.ambiguous ? (
                  <div className="callout__sub">
                    that fits more than one answer — be more specific
                  </div>
                ) : null}
                {result?.verdict === "pass" ? (
                  <div className="callout__sub">
                    {result.playerName} had nothing
                  </div>
                ) : null}
                {result?.verdict === "correct" && result.points ? (
                  <div className="callout__sub numeral">+{result.points}</div>
                ) : null}
                {result?.nearMiss ? (
                  <div className="callout__sub">
                    {result.nearMiss.text} was #{result.nearMiss.rank}
                  </div>
                ) : null}
                {result?.verdict === "duplicate" ? (
                  <div className="callout__sub">already on the board</div>
                ) : null}
              </m.div>
            ) : null}
          </AnimatePresence>
        </>
      )}
      <Tuner />
    </m.div>
  );
}

function Lobby({
  room,
  qr,
  joinUrl,
  error,
}: {
  room: NonNullable<ReturnType<typeof useRoom>>;
  qr: string;
  joinUrl: string;
  error: string | null;
}) {
  const lobby = useMotionTuning().lobby;
  const canStart =
    room.players.some((p) => p.teamId === "rose") &&
    room.players.some((p) => p.teamId === "slate");
  // Only players actually connected can tap ready, so only they are counted.
  const present = room.players.filter((p) => p.connected);
  const readyCount = present.filter((p) => p.ready).length;

  /*
   * Each piece of the poster is stamped onto the page in turn rather than
   * simply being there. Entry lives on a wrapper and only touches opacity,
   * scale and rise — the resting tilt and the idle drift stay on the inner
   * element in CSS, so the two never write `transform` at the same time.
   */
  const stamp = (index: number) => ({
    initial: { opacity: 0, y: lobby.stampRise_px, scale: lobby.stampScaleFrom },
    animate: { opacity: 1, y: 0, scale: 1 },
    transition: {
      type: "spring" as const,
      stiffness: lobby.stamp_stiffness,
      damping: lobby.stamp_damping,
      delay: (index * lobby.stampStagger_ms) / 1000,
    },
  });

  const status = error
    ? error
    : present.length === 0
      ? "Waiting for players to join"
      : !canStart
        ? "Both teams need at least one player"
        : readyCount === present.length
          ? "Starting…"
          : // Sentinel, not copy: this state renders as a sentence with the
            // phone's own ready chip embedded, so the string is just the key.
            "tap-ready";

  return (
    <div
      className="lobby"
      style={{
        ["--lobby-drift-deg" as string]: `${lobby.driftAmplitude_deg}deg`,
        ["--lobby-drift-px" as string]: `${lobby.driftRise_px}px`,
        ["--lobby-drift-ms" as string]: `${lobby.driftDuration_ms}ms`,
        ["--lobby-seat-ms" as string]: `${lobby.seatPulseDuration_ms}ms`,
        ["--lobby-nudge-ms" as string]: `${lobby.nudgePulse_ms}ms`,
        ["--lobby-nudge-scale" as string]: lobby.nudgeScale,
      }}
    >
      {/* Masthead, code and QR are one poster, not three things that happen to
          be on the same screen — they all say "join this room". Set as a single
          centred block so the leftover height falls outside it as margin. */}
      <div className="lobby__hero">
        <div className="lobby__brand">
          <m.div {...stamp(0)}>
            <h1 className="lobby__title display riso" data-text="Fact Feud">
              Fact Feud
            </h1>
          </m.div>
          <m.div {...stamp(1)}>
            <div className="lobby__join">
              {joinUrl.replace(/^https?:\/\//, "") || "…"}
            </div>
          </m.div>
          <m.div {...stamp(2)}>
            <div className="lobby__code numeral">{room.code}</div>
          </m.div>
        </div>
        {qr ? (
          <m.div {...stamp(3)}>
            <div className="lobby__qr">
              <img src={qr} alt={`QR code to join room ${room.code}`} />
            </div>
          </m.div>
        ) : null}
      </div>

      <footer className="lobby__foot">
        <div className="rosters">
          {(["rose", "slate"] as const).map((teamId) => {
            const squad = room.players.filter((p) => p.teamId === teamId);
            return (
              <div className="roster teamTint" data-team={teamId} key={teamId}>
                <div className="roster__name">{room.teams[teamId].name}</div>
                <ul>
                  {/* An empty bench as a blank card reads as a rendering fault.
                      A held seat reads as a bench with nobody on it yet. */}
                  {squad.length === 0 ? (
                    <li className="roster__seat">seat free</li>
                  ) : (
                    squad.map((p) => (
                      // Arriving is the one moment a name gets attention, so it
                      // overshoots before it settles.
                      <m.li
                        key={p.id}
                        data-ready={p.ready}
                        initial={{ opacity: 0, scale: 0.6, y: 10 }}
                        animate={{
                          opacity: 1,
                          scale: [null, lobby.joinOvershoot_scale, 1],
                          y: 0,
                        }}
                        transition={{
                          type: "spring",
                          stiffness: lobby.stamp_stiffness,
                          damping: lobby.stamp_damping,
                        }}
                      >
                        {p.name}
                        <span aria-hidden="true">{p.ready ? " ✓" : ""}</span>
                        <span className="srOnly">
                          {p.ready ? " is ready" : " is not ready"}
                        </span>
                      </m.li>
                    ))
                  )}
                </ul>
              </div>
            );
          })}
        </div>
        {/* Keyed on the text so a change swaps lines rather than silently
            rewriting one in place — the room should notice it moved on. */}
        <AnimatePresence mode="wait" initial={false}>
          <m.p
            key={status}
            className="subtitle lobby__status"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.24 }}
          >
            {status === "tap-ready" ? (
              <>
                Tap <span className="lobby__readyChip">ready</span> on your
                phone
              </>
            ) : (
              status
            )}
          </m.p>
        </AnimatePresence>
      </footer>
    </div>
  );
}

function Final({ room }: { room: NonNullable<ReturnType<typeof useRoom>> }) {
  const winner = room.winner;
  const winningTeam = winner && winner !== "tie" ? room.teams[winner] : null;
  const best = [...room.players].sort(
    (a, b) => b.correctCount - a.correctCount,
  )[0];

  return (
    <div
      className="final teamTint"
      data-team={winner === "tie" ? "rose" : (winner ?? "rose")}
    >
      <div className="label">Final</div>
      <m.div
        className="final__winner display riso"
        data-text={winningTeam ? `${winningTeam.name} win` : "A dead heat"}
        initial={{ opacity: 0, scale: 0.9, rotate: -6 }}
        animate={{ opacity: 1, scale: 1, rotate: -1.1 }}
        transition={{ type: "spring", stiffness: 200, damping: 14 }}
      >
        {winningTeam ? `${winningTeam.name} win` : "A dead heat"}
      </m.div>
      <div className="final__scores">
        <div>
          <div className="label">{room.teams.rose.name}</div>
          <div className="team__score numeral">{room.teams.rose.score}</div>
        </div>
        <div>
          <div className="label">{room.teams.slate.name}</div>
          <div className="team__score numeral">{room.teams.slate.score}</div>
        </div>
      </div>
      {best && best.correctCount > 0 ? (
        <p className="subtitle">
          Sharpest in the room: {best.name}, {best.correctCount} answers
        </p>
      ) : null}
    </div>
  );
}
